//@import dependencies
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Spinner } from 'react-bootstrap'
import { FaFacebook, FaFileDownload, FaLinkedin, FaTwitter } from 'react-icons/fa'
import { AiOutlineSafetyCertificate, AiFillCloseCircle, AiOutlineShareAlt, AiOutlineEye } from 'react-icons/ai'
import { FiExternalLink } from 'react-icons/fi'
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import moment from 'moment'
//@end

//@import components
import DataTableComponent from 'app_components/commons/dataTable'
// import PdfViewerComponent from 'app_components/commons/pdfViewer'
//@end

// @import hooks
import { useColors } from 'app_hooks/useColors'
// @end

// @INFO Import types
import { IUserCertification } from 'app_types/campus/user/userInfoTypes'
// @end

// @INFO Import services
import CertificateService from 'app_services/user/certificateService'
// @end

// @INFO Import Styles
import './../modalUserCertificationsModule.scss'

// @ADD Component types
interface IModalUserCertificationsModuleLayoutProps {
  open: boolean
  handleClose: (data: any) => void
  certifications: IUserCertification[]
}

const ModalUserCertificationsModuleLayout: React.FC<IModalUserCertificationsModuleLayoutProps> = (props) => {

  const certificateService: CertificateService = new CertificateService()
  const { t } = useTranslation()
  const [colors] = useColors()

  const [pdfPreview, setPdfPreview] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({ status: 'initial', file: null })

  const [pdfDownload, setPdfDownload] = useState<{
    status: 'initial' | 'loaded' | 'loading',
    file: any | null
  }>({ status: 'initial', file: null })

  const { open } = props

  const handleCloseModal = (data: any) => {
    props.handleClose(data)
  }

  const handlePreviewFile = async (status: boolean, certificate?: IUserCertification) => {
    if (status === true && certificate) {
      setPdfPreview({ status: 'loading', file: null })
      if (certificate.imagePath) {
        setPdfPreview({ status: 'loaded', file: certificate.imagePath })
      } else {
        const responsePreview = await certificateService.loadPreview(certificate.hash, {
          format: 1,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id
        })
        if (responsePreview && responsePreview.certificate?.imagePath) {
          setPdfPreview({ status: 'loaded', file: responsePreview.certificate?.imagePath })
          certificate.imagePath = responsePreview.certificate?.imagePath
        } else {
          setPdfPreview({ status: 'initial', file: null })
        }
      }
    } else {
      setPdfPreview({ status: 'initial', file: null })
    }
  }

  const handleDownloadFile = async (status: boolean, certificate?: IUserCertification) => {
    if (status === true && certificate) {
      setPdfDownload({ status: 'loading', file: null })
      if (certificate.pdfPath) {
        // update download file date from Student dashboard:
        certificate.downloadDate = moment().format('YYYY-MM-DD HH:mm:ss').replace(' ', 'T');
        const respUpdateDate = await certificateService.updateCertificationFirstDownloadDate(certificate);

        setPdfDownload({ status: 'loaded', file: certificate.pdfPath })
        window.open(certificate.pdfPath, '_blank');
      } else {
        const responsePreview = await certificateService.loadPreview(certificate.hash, {
          format: 2,
          template: 1,
          updateCertificate: true,
          certificate_queue: certificate._id
        })
        if (responsePreview && responsePreview.certificate?.pdfPath) {
          // update download file date from Student dashboard:
          certificate.downloadDate = moment().format('YYYY-MM-DD HH:mm:ss').replace(' ', 'T');
          const respUpdateDate = await certificateService.updateCertificationFirstDownloadDate(certificate);

          setPdfDownload({ status: 'loaded', file: responsePreview.certificate?.pdfPath })
          certificate.pdfPath = responsePreview.certificate?.pdfPath
          window.open(responsePreview.certificate?.pdfPath, '_blank');
        } else {
          setPdfDownload({ status: 'initial', file: null })
        }
      }
    } else {
      setPdfDownload({ status: 'initial', file: null })
    }
  }

  const shareTwitter = (id: string) => {
    window.open(`https://twitter.com/intent/tweet?hashtags=HuellaDeConfianza&text=Ya+puedes+consultar+mi+certificado&url=https%3A%2F%2Fwww.huelladeconfianza.com%2Fcertificado%2F${id}%2F&via=icontec`, '_blank');
  }

  const shareFacebook = (id: string) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=https://www.huelladeconfianza.com/certificado/${id}`, '_blank');
  }

  const shareLinkedin = (certificate: IUserCertification) => {
    let url = "https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&";
    url += `name=${certificate.title}&`
    url += "organizationId=1298608&";
    url += "issueYear=" + certificate?.date.substring(0, 4) + "&";
    url += "issueMonth=" + certificate?.date.substring(5, 7) + "&";
    url += `certUrl=https://www.huelladeconfianza.com/certificado/${certificate.hash}/&`;
    url += "certId=" + certificate.hash;
    const url_encoded = encodeURI(url);
    window.open(url_encoded, '_blank');
  }

  return (
    <div className=''>
      <Modal
        show={open}
        onHide={() => handleCloseModal({ status: false })}
        className="modal_user_certifications-component"
        size='lg'
      >
        <Modal.Header>
          <Modal.Title>
            {t('modules.modal_user_certifications.title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='alert alert-info'>
            {parse(t('modules.modal_user_certifications.notification'))}
          </div>
          <DataTableComponent
            data={props.certifications}
            headerKeys={[
              `${t('modules.modal_user_certifications.table.title')}`,
              `${t('modules.modal_user_certifications.table.date')}`,
            ]}
            keys={['title', 'date']}
            detailsKey={[]}
            onModal={null}
            rowButtons={(certification: IUserCertification) => {
              return (
                <>
                  <button
                    className='btn btn-link btn-sm'
                    data-tip={t('modules.modal_user_certifications.actions.preview')}
                    onClick={() => handlePreviewFile(true, certification)}
                    disabled={pdfPreview.status === 'loading'}
                  >
                    <AiOutlineEye size="20px" color={colors.primary} />
                  </button>
                  <button
                    className='btn btn-link btn-sm'
                    data-tip={t('modules.modal_user_certifications.actions.download')}
                    onClick={() => handleDownloadFile(true, certification)}
                    disabled={pdfDownload.status === 'loading'}
                  >
                    {pdfDownload.status === 'loading' ? (
                      <Spinner className='spinner-loading' animation='border' size='sm' />
                    ) : (
                      <FaFileDownload size="20px" color={colors.primary} />
                    )}
                  </button>

                  {certification.url && (
                    <Link
                      to={{ pathname: `${certification.url}` }}
                      target='_blank'
                    >
                      <button
                        className='btn btn-link btn-sm'
                        data-tip={t('modules.modal_user_certifications.actions.emitter_validation')}
                      >
                        <FiExternalLink size="20px" color={colors.primary} />
                      </button>
                    </Link>
                  )}

                  <button
                    className='btn btn-link btn-sm'
                    data-tip={t('modules.modal_user_certifications.actions.share_twitter')}
                    onClick={() => shareTwitter(certification.hash)}
                    disabled={pdfDownload.status === 'loading'}
                  >
                    {pdfDownload.status === 'loading' ? (
                      <Spinner className='spinner-loading' animation='border' size='sm' />
                    ) : (
                      <FaTwitter size="20px" color={colors.primary} />
                    )}
                  </button>
                  <button
                    className='btn btn-link btn-sm'
                    data-tip={t('modules.modal_user_certifications.actions.share_facebook')}
                    onClick={() => shareFacebook(certification.hash)}
                    disabled={pdfDownload.status === 'loading'}
                  >
                    {pdfDownload.status === 'loading' ? (
                      <Spinner className='spinner-loading' animation='border' size='sm' />
                    ) : (
                      <FaFacebook size="20px" color={colors.primary} />
                    )}
                  </button>
                  <button
                    className='btn btn-link btn-sm'
                    data-tip={t('modules.modal_user_certifications.actions.share_linkedin')}
                    onClick={() => shareLinkedin(certification)}
                    disabled={pdfDownload.status === 'loading'}
                  >
                    {pdfDownload.status === 'loading' ? (
                      <Spinner className='spinner-loading' animation='border' size='sm' />
                    ) : (
                      <FaLinkedin size="20px" color={colors.primary} />
                    )}
                  </button>

                </>
              )
            }}
            tooltipKeys={['title']}
          />
          {pdfPreview.status === 'loaded' && pdfPreview.file ? (
            <>
              <hr />
              <div className="text-right">
                <button
                  className='btn btn-link btn-sm'
                  data-tip={t('modules.modal_user_certifications.actions.close_preview')}
                  onClick={() => handlePreviewFile(false)}
                >
                  <AiFillCloseCircle size="20px" color={colors.primary} />
                </button>
              </div>
              {/* <img style={{width: '100%'}} src={`data:image/png;base64,${pdfPreview.file}`} alt='preview'/> */}
              <img style={{ width: '100%' }} src={pdfPreview.file} alt='preview' />
              {/* <PdfViewerComponent
                  file={pdfPreview.file}
                /> */}
            </>
          ) : (
            <>
              {pdfPreview.status === 'loading' && (
                <div className='text-center'>
                  <Spinner animation="border" className='icon-loading' />
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ModalUserCertificationsModuleLayout
